import { useState } from "react";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, User, Loader2, Copy, ExternalLink } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useBusiness } from "@/hooks/use-business";
import { getBaseUrl } from "@/lib/site";
import { useStaff } from "@/hooks/use-staff";
import { useLocations } from "@/hooks/use-locations";
import { useToast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";
import { Checkbox } from "@/components/ui/checkbox";
import { useLocationContext } from "@/contexts/LocationContext";
import { phoneToE164 } from "@/lib/phone-utils";

type AppRole = Tables<"staff">["role"];

export default function Staff() {
  const { business, loading: businessLoading } = useBusiness();
  const { staff, loading: staffLoading, createStaff, updateStaff, deleteStaff } = useStaff();
  const { locations, loading: locationsLoading } = useLocations();
  const { toast } = useToast();
  const { selectedLocationId } = useLocationContext();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formRole, setFormRole] = useState<AppRole>("staff");
  const [formSelectedLocations, setFormSelectedLocations] = useState<string[]>([]);
  const [formPortalEnabled, setFormPortalEnabled] = useState(false);
  const [formPortalRole, setFormPortalRole] = useState<"service_provider" | "manager">("service_provider");

  const filteredStaff = staff.filter((member) => {
    const matchesSearch = 
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    
    // Location filter: check if staff is assigned to the selected location
    // If staff has no location assignments, they're available at all locations
    const matchesLocation = !selectedLocationId || (() => {
      const staffLocationIds = (member as any).assignedLocationIds || [];
      // If staff has no location assignments, they're available at all locations
      if (staffLocationIds.length === 0) return true;
      // Otherwise, check if they're assigned to the selected location
      return staffLocationIds.includes(selectedLocationId);
    })();
    
    return matchesSearch && matchesLocation;
  });

  const resetForm = () => {
    setFormName("");
    setFormEmail("");
    setFormPhone("");
    setFormRole("staff");
    setFormSelectedLocations([]);
    setFormPortalEnabled(false);
    setFormPortalRole("service_provider");
    setEditingStaffId(null);
  };

  const handleEditStaff = (member: typeof staff[0]) => {
    setEditingStaffId(member.id);
    setFormName(member.name);
    setFormEmail(member.email || "");
    setFormPhone(member.phone || "");
    setFormRole(member.role);
    setFormSelectedLocations((member as any).assignedLocationIds || []);
    setFormPortalEnabled((member as any).portal_enabled || false);
    setFormPortalRole((member as any).portal_role || "service_provider");
    setIsDialogOpen(true);
  };

  const handleSaveStaff = async () => {
    if (!formName.trim()) return;

    setIsSaving(true);
    try {
      const normalizedPhone = formPhone.trim() ? (phoneToE164(formPhone.trim(), "233") || formPhone.trim()) : null;
      const staffData: any = {
          name: formName,
          email: formEmail || null,
          phone: normalizedPhone,
          role: formRole,
        portal_enabled: formPortalEnabled,
        portal_role: formPortalEnabled ? formPortalRole : null,
      };

      if (editingStaffId) {
        await updateStaff(
          editingStaffId,
          staffData,
          formSelectedLocations
        );
      } else {
        await createStaff(
          staffData,
          formSelectedLocations
        );
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save staff member",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStaff = async (id: string) => {
    try {
      await deleteStaff(id);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove staff member",
        variant: "destructive",
      });
    }
  };

  const isLoading = businessLoading || staffLoading;
  const primaryModule = (business as any)?.primary_module || "appointments";
  const isCatering = primaryModule === "catering_services" || primaryModule === "food_vendors";

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title={isCatering ? "Delivery Team" : "Staff"}
        description={isCatering ? "Manage your delivery riders and portal access" : "Manage your team members and their roles"}
        action={
          <Button size="pill" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            {isCatering ? "Add Rider" : "Add Staff"}
          </Button>
        }
      />

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search staff..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Staff List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredStaff.length > 0 ? (
        <div className="bg-card rounded-xl border border-border shadow-soft overflow-hidden">
          <div className="divide-y divide-border">
            {filteredStaff.map((member) => (
              <div
                key={member.id}
                className="p-4 hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
                      {member.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-foreground truncate">{member.name}</h3>
                      <p className="text-sm text-muted-foreground truncate">{member.email || "No email"}</p>
                      {(member as any).portal_enabled && (member as any).portal_id && (
                        <p className="text-xs text-muted-foreground mt-1 font-mono truncate">
                          Portal: {(member as any).portal_id} ({(member as any).portal_role === "manager" ? "Manager" : "Provider"})
                        </p>
                      )}
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditStaff(member)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleDeleteStaff(member.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="text-xs capitalize">
                    {member.role}
                  </Badge>
                  <StatusBadge status={member.active ? "active" : "inactive"} />
                  {(member as any).portal_enabled && (
                    <Badge variant="outline" className="text-xs">
                      Portal Active
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-soft">
          <EmptyState
            icon={User}
            title="No staff members found"
            description="Add team members to help manage your business"
            action={{
              label: "Add Staff",
              onClick: () => { resetForm(); setIsDialogOpen(true); },
            }}
          />
        </div>
      )}

      {/* Add/Edit Staff Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
        <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{editingStaffId ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
            <DialogDescription>
              {editingStaffId ? "Update staff member details" : "Add a new team member to your business"}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., John Doe"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 555-0123"
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value="Staff"
                  readOnly
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">
                  Staff members use portal access. Only business owners can login to the platform.
                </p>
              </div>
              {locations.length > 0 && (
                <div className="space-y-2">
                  <Label>Locations</Label>
                  <p className="text-xs text-muted-foreground">
                    Select locations where this staff member works. Leave empty for all locations.
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {locations.map((location) => (
                      <div key={location.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`location-${location.id}`}
                          checked={formSelectedLocations.includes(location.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormSelectedLocations([...formSelectedLocations, location.id]);
                            } else {
                              setFormSelectedLocations(
                                formSelectedLocations.filter((id) => id !== location.id)
                              );
                            }
                          }}
                        />
                        <Label
                          htmlFor={`location-${location.id}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {location.name}
                          {location.is_default && (
                            <span className="text-xs text-muted-foreground ml-2">(Default)</span>
                          )}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <Separator />
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Portal Access</Label>
                      <p className="text-xs text-muted-foreground">
                        Enable anonymous portal access for this staff member
                      </p>
                    </div>
                    <Switch
                      checked={formPortalEnabled}
                      onCheckedChange={setFormPortalEnabled}
                    />
                  </div>
                </div>
                
                {formPortalEnabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="portal-role">Portal Role</Label>
                      <Select 
                        value={formPortalRole} 
                        onValueChange={(value: "service_provider" | "manager") => setFormPortalRole(value)}
                      >
                  <SelectTrigger>
                          <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                          <SelectItem value="service_provider">Service Provider (View Only)</SelectItem>
                          <SelectItem value="manager">Manager (Can Edit & Reassign)</SelectItem>
                  </SelectContent>
                </Select>
                      <p className="text-xs text-muted-foreground">
                        {formPortalRole === "service_provider" 
                          ? "Can view assigned bookings and mark as completed/no-show"
                          : "Can view all bookings for assigned locations, edit, and reassign"}
                      </p>
                    </div>
                    
                    {editingStaffId && (staff.find(s => s.id === editingStaffId) as any)?.portal_id && (
                      <div className="space-y-2">
                        <Label>Portal ID</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={(staff.find(s => s.id === editingStaffId) as any)?.portal_id || ""}
                            readOnly
                            className="font-mono"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={async () => {
                              const portalId = (staff.find(s => s.id === editingStaffId) as any)?.portal_id;
                              if (portalId) {
                                const portalUrl = `${getBaseUrl()}/portal/${portalId}`;
                                await navigator.clipboard.writeText(portalUrl);
                                toast({
                                  title: "Portal link copied",
                                  description: "Share this link with the staff member",
                                });
                              }
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              const portalId = (staff.find(s => s.id === editingStaffId) as any)?.portal_id;
                              if (portalId) {
                                window.open(`${getBaseUrl()}/portal/${portalId}`, "_blank");
                              }
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Portal ID will be generated automatically when portal is enabled
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 rounded-lg">
              Cancel
            </Button>
            <Button onClick={handleSaveStaff} disabled={isSaving} className="flex-1 rounded-lg">
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingStaffId ? "Save Changes" : "Add Staff"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
